import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAssignableWorkers } from "../apis/fmsPosApi";
import { addProvider } from "../apis/addProvider";
import { PROVIDER_TYPE_DSW, PROVIDER_TYPE_VENDOR, PLACEHOLDER_PROVIDER_NAME, getLocalProviders, saveLocalProvider } from "./sdp";

// For now the list shows only the company's fetched workers/suppliers: no "To be determined"
// placeholders, no browser-only "not linked" providers, no "+ Add new worker / supplier".
// Set to true to bring those back.
const SHOW_EXTRA_PROVIDER_OPTIONS = false;

// The company's assignable workers/vendors (same list as the .NET Assign Worker popup), loaded once
// per page and shared by every picker (and by ServiceTableV2 for each provider's Worker/Vendor type);
// a failed load is retried next time. Call it only after business login, so it lists the right company.
let assignableWorkersPromise = null;
export function loadAssignableWorkers() {
    if (!assignableWorkersPromise) {
        assignableWorkersPromise = getAssignableWorkers()
            .then(list => list.map(w => ({
                workerId: w.WorkerId,
                name: w.Name || '',
                providerType: w.ProviderType === 'Vendor' || Number(w.IsVendor) === 1 ? PROVIDER_TYPE_VENDOR : PROVIDER_TYPE_DSW,
            })))
            .catch(error => {
                console.error('Error loading assignable workers:', error);
                assignableWorkersPromise = null;
                return [];
            });
    }
    return assignableWorkersPromise;
}

// Split a typed name into first/last for a new worker ("Doe, Jane" or "Jane Doe")
function splitName(text) {
    const s = (text || '').trim();
    if (s.includes(',')) {
        const [last, ...rest] = s.split(',');
        return { firstName: rest.join(',').trim(), lastName: last.trim() };
    }
    const parts = s.split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

/**
 * Type-ahead provider picker for an SDP spending plan line. Resolves to a real worker/vendor record
 * ({ workerId, name, providerType }), a "To be determined" placeholder (workerId null), or a
 * newly created provider. Tab/Shift+Tab/Enter are handed back to the row via onNavigate once a
 * choice is made, so staff can key a paper plan without the mouse.
 *
 * localProviders: providers already known on this page (e.g. the patient's assigned workers),
 * matched instantly while the server search is in flight.
 */
export default function ProviderPicker({
    value,
    providerType,
    localProviders = [],
    enableSearch = true,
    onSelect,
    onNavigate,
    onClose,
}) {
    const [query, setQuery] = useState(value || '');
    const [remoteResults, setRemoteResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState({ providerType: providerType || PROVIDER_TYPE_DSW, firstName: '', lastName: '', name: '' });
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState('');
    const inputRef = useRef(null);
    // Set once a choice is handed back, so the blur that follows unmounting can't overwrite it
    const doneRef = useRef(false);
    const typed = query.trim() !== (value || '').trim();

    useEffect(() => { inputRef.current?.focus(); }, []);

    // The table scrolls/clips its cells, so the menu is portalled to <body> and pinned to the
    // input; it opens upward when there isn't room below
    const MENU_WIDTH = 288;
    const MENU_MAX_HEIGHT = 280;
    const [menuPos, setMenuPos] = useState(null);
    useLayoutEffect(() => {
        const place = () => {
            const rect = inputRef.current?.getBoundingClientRect();
            if (!rect) return;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUp = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow;
            const left = Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8));
            setMenuPos(openUp
                ? { left, bottom: window.innerHeight - rect.top + 4, maxHeight: Math.min(MENU_MAX_HEIGHT, rect.top - 8) }
                : { left, top: rect.bottom + 4, maxHeight: Math.min(MENU_MAX_HEIGHT, spaceBelow - 8) });
        };
        place();
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, []);

    // The company's workers/vendors, filtered below as they type
    useEffect(() => {
        if (!enableSearch) return undefined;
        let cancelled = false;
        setIsSearching(true);
        loadAssignableWorkers().then(list => {
            if (cancelled) return;
            setRemoteResults(list);
            setIsSearching(false);
        });
        return () => { cancelled = true; };
    }, [enableSearch]);

    const options = useMemo(() => {
        const q = query.trim().toLowerCase();
        const seen = new Set();
        const matches = [];
        // The company list first: its Worker/Vendor type comes straight from the worker profile
        [...remoteResults, ...localProviders].forEach(p => {
            if (!p?.workerId || seen.has(String(p.workerId))) return;
            if (q && typed && !p.name.toLowerCase().includes(q)) return;
            seen.add(String(p.workerId));
            matches.push({ kind: 'provider', ...p });
        });
        if (!SHOW_EXTRA_PROVIDER_OPTIONS) return matches.slice(0, 50);
        // Providers added in this browser only (no record id yet)
        const localMatches = getLocalProviders()
            .filter(p => !q || !typed || p.name.toLowerCase().includes(q))
            .map(p => ({ kind: 'local', ...p }));
        return [
            ...matches.slice(0, 50),
            ...localMatches.slice(0, 5),
            { kind: 'placeholder', name: PLACEHOLDER_PROVIDER_NAME, providerType: PROVIDER_TYPE_DSW, label: `${PLACEHOLDER_PROVIDER_NAME} (worker)` },
            { kind: 'placeholder', name: PLACEHOLDER_PROVIDER_NAME, providerType: PROVIDER_TYPE_VENDOR, label: `${PLACEHOLDER_PROVIDER_NAME} (supplier)` },
            ...(enableSearch ? [{ kind: 'add', label: query.trim() ? `+ Add new worker / supplier "${query.trim()}"` : '+ Add new worker / supplier' }] : []),
        ];
    }, [localProviders, remoteResults, query, typed, enableSearch]);

    const [usedArrows, setUsedArrows] = useState(false);
    useEffect(() => { setHighlight(0); setUsedArrows(false); }, [query]);

    const choose = (option, navigateKey) => {
        if (option.kind === 'add') {
            const { firstName, lastName } = splitName(query);
            setAddForm(prev => ({ ...prev, firstName, lastName, name: query.trim() }));
            setAddError('');
            setShowAddForm(true);
            return;
        }
        const selection = {
            workerId: option.kind === 'provider' ? option.workerId : null,
            name: option.name,
            providerType: option.providerType,
        };
        doneRef.current = true;
        onSelect(selection);
        if (navigateKey) onNavigate?.(navigateKey, selection);
        else onClose?.();
    };

    // Leaving the field without choosing keeps what they typed as an unlinked name, which saves
    // and rolls up like a placeholder but blocks Activate until it is linked
    const keepTyped = (navigateKey) => {
        if (doneRef.current) return;
        doneRef.current = true;
        const selection = typed ? { workerId: null, name: query.trim(), providerType: providerType || PROVIDER_TYPE_DSW } : undefined;
        if (selection) onSelect(selection);
        if (navigateKey) onNavigate?.(navigateKey, selection);
        else onClose?.();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setUsedArrows(true); setHighlight(h => Math.min(h + 1, options.length - 1)); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); setUsedArrows(true); setHighlight(h => Math.max(h - 1, 0)); return; }
        if (e.key === 'Escape') { e.preventDefault(); doneRef.current = true; onClose?.(); return; }
        if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault();
            const navigateKey = e.key === 'Enter' ? 'Enter' : (e.shiftKey ? 'ShiftTab' : 'Tab');
            const option = options[highlight];
            // Take the highlighted option if it's a real match or they arrowed to it; otherwise a
            // typed name with no match is kept as typed rather than silently becoming a placeholder
            if (option && (option.kind === 'provider' || option.kind === 'local' || usedArrows)) {
                choose(option, option.kind === 'add' ? null : navigateKey);
            } else if (typed && query.trim()) {
                keepTyped(navigateKey);
            } else {
                doneRef.current = true;
                onNavigate?.(navigateKey);
            }
        }
    };

    const handleAddProvider = async () => {
        const isVendor = addForm.providerType === PROVIDER_TYPE_VENDOR;
        if (isVendor ? !addForm.name.trim() : !(addForm.firstName.trim() && addForm.lastName.trim())) {
            setAddError(isVendor ? 'Enter the supplier name' : 'Enter first and last name');
            return;
        }
        setIsAdding(true);
        setAddError('');
        try {
            const payload = isVendor
                ? { ProviderType: PROVIDER_TYPE_VENDOR, Name: addForm.name.trim() }
                : { ProviderType: PROVIDER_TYPE_DSW, FirstName: addForm.firstName.trim(), LastName: addForm.lastName.trim() };
            const name = isVendor ? addForm.name.trim() : `${addForm.lastName.trim()}, ${addForm.firstName.trim()}`;
            let selection;
            try {
                const result = await addProvider(payload);
                if (!result?.WorkerId) throw new Error(result?.message || 'Provider was not created');
                selection = { workerId: result.WorkerId, name: result.Name || name, providerType: addForm.providerType };
            } catch (err) {
                // Endpoint not built yet: keep the provider on the front end only
                if (err?.status !== 404) throw err;
                selection = { workerId: null, name, providerType: addForm.providerType };
                saveLocalProvider({ name, providerType: addForm.providerType });
            }
            doneRef.current = true;
            onSelect(selection);
            onNavigate?.('Tab', selection);
        } catch (err) {
            setAddError(err?.message || 'Could not add provider');
        } finally {
            setIsAdding(false);
        }
    };

    return (
        <div className="relative" onClick={e => e.stopPropagation()}>
            <input
                ref={inputRef}
                type="text"
                className="w-full border rounded px-1"
                value={query}
                placeholder="Search provider"
                onChange={e => { setQuery(e.target.value); setShowAddForm(false); }}
                onKeyDown={handleKeyDown}
                onBlur={() => { if (!showAddForm) keepTyped(null); }}
            />
            {menuPos && createPortal(
            <div
                className="fixed z-50 bg-white border rounded shadow-lg text-left text-sm overflow-y-auto"
                style={{ left: menuPos.left, top: menuPos.top, bottom: menuPos.bottom, width: MENU_WIDTH, maxHeight: menuPos.maxHeight }}
                onClick={e => e.stopPropagation()}
            >
                {!showAddForm ? (
                    <ul className="py-1">
                        {isSearching && <li className="px-3 py-1 text-xs text-gray-400">Loading workers…</li>}
                        {!isSearching && options.length === 0 && <li className="px-3 py-1 text-xs text-gray-400">No matching workers or suppliers</li>}
                        {options.map((option, i) => (
                            <li
                                key={`${option.kind}-${option.workerId || option.providerType || ''}-${i}`}
                                className={`px-3 py-1 cursor-pointer flex justify-between gap-2 ${i === highlight ? 'bg-blue-100' : 'hover:bg-gray-100'} ${option.kind !== 'provider' ? 'text-gray-600 italic' : ''}`}
                                onMouseDown={e => { e.preventDefault(); choose(option, null); }}
                                onMouseEnter={() => setHighlight(i)}
                            >
                                <span>{option.label || option.name}</span>
                                {(option.kind === 'provider' || option.kind === 'local') && (
                                    <span className="text-xs text-gray-400 not-italic">
                                        {option.providerType === PROVIDER_TYPE_VENDOR ? 'Supplier' : 'Worker'}{option.kind === 'local' ? ' · not linked' : ''}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="p-3 space-y-2" onMouseDown={e => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }}>
                        <div className="flex gap-3 text-xs">
                            {[PROVIDER_TYPE_DSW, PROVIDER_TYPE_VENDOR].map(t => (
                                <label key={t} className="flex items-center gap-1">
                                    <input type="radio" checked={addForm.providerType === t} onChange={() => setAddForm(prev => ({ ...prev, providerType: t }))} />
                                    {t === PROVIDER_TYPE_VENDOR ? 'Supplier' : 'Worker'}
                                </label>
                            ))}
                        </div>
                        {addForm.providerType === PROVIDER_TYPE_VENDOR ? (
                            <input autoFocus type="text" className="w-full border rounded px-1" placeholder="Supplier name"
                                value={addForm.name} onChange={e => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddProvider(); } if (e.key === 'Escape') setShowAddForm(false); }} />
                        ) : (
                            <div className="flex gap-1">
                                <input autoFocus type="text" className="w-1/2 border rounded px-1" placeholder="First name"
                                    value={addForm.firstName} onChange={e => setAddForm(prev => ({ ...prev, firstName: e.target.value }))}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddProvider(); } if (e.key === 'Escape') setShowAddForm(false); }} />
                                <input type="text" className="w-1/2 border rounded px-1" placeholder="Last name"
                                    value={addForm.lastName} onChange={e => setAddForm(prev => ({ ...prev, lastName: e.target.value }))}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddProvider(); } if (e.key === 'Escape') setShowAddForm(false); }} />
                            </div>
                        )}
                        {addError && <div className="text-xs text-red-600">{addError}</div>}
                        <div className="flex gap-2 justify-end">
                            <button type="button" className="px-2 py-0.5 text-xs rounded bg-gray-200" onClick={() => { setShowAddForm(false); inputRef.current?.focus(); }}>Cancel</button>
                            <button type="button" disabled={isAdding} className="px-2 py-0.5 text-xs rounded bg-primary-dark text-white disabled:opacity-50" onClick={handleAddProvider}>
                                {isAdding ? 'Adding…' : 'Add'}
                            </button>
                        </div>
                    </div>
                )}
            </div>,
            document.body
            )}
        </div>
    );
}
